'use client'

import React, { useId, useMemo } from 'react'
import { X } from 'lucide-react'
import { BsArrowsFullscreen } from 'react-icons/bs'

import {
  buildApartmentFloorPlanSlides,
  buildApartmentGallerySlides,
  type ApartmentGalleryViewId,
  type ApartmentGalleryViews,
} from '@/lib/apartments/apartmentGallery'
import { cn } from '@/utilities/ui'

import { RoomGalleryCarousel } from './RoomGalleryCarousel'
import type { RoomGalleryCopy } from './RoomGalleryComparisonSlide'
import { RoomGalleryVideoPanel } from './RoomGalleryVideoPanel'
import {
  RoomGalleryViewTabs,
  galleryComparisonLabelClassName,
  galleryOverlayControlClassName,
} from './RoomGalleryViewTabs'

export type RoomGalleryViewerCopy = RoomGalleryCopy & {
  ariaLabel: string
  closeFullscreen: string
  next: string
  openFullscreen: string
  position: string
  previous: string
  tabs: Record<ApartmentGalleryViewId, string>
  tabsAriaLabel: string
  floorPlansAriaLabel: string
  floorPlansPanelAriaLabel: string
  view360PanelAriaLabel: string
}

type RoomGalleryViewerProps = {
  activeView: ApartmentGalleryViewId
  carouselKey: string
  className?: string
  copy: RoomGalleryViewerCopy
  fullscreenButtonRef?: React.RefObject<HTMLButtonElement | null>
  gallerySlideIndex: number
  floorPlanSlideIndex: number
  mode: 'inline' | 'fullscreen'
  objectFit?: 'cover' | 'contain'
  onActiveViewChange: (view: ApartmentGalleryViewId) => void
  onGallerySlideIndexChange: (index: number) => void
  onFloorPlanSlideIndexChange: (index: number) => void
  onRequestClose?: () => void
  onRequestFullscreen?: () => void
  enabledTabs: ApartmentGalleryViewId[]
  views: ApartmentGalleryViews
}

export function RoomGalleryViewer({
  activeView,
  carouselKey,
  className,
  copy,
  fullscreenButtonRef,
  gallerySlideIndex,
  floorPlanSlideIndex,
  mode,
  objectFit = 'cover',
  onActiveViewChange,
  onGallerySlideIndexChange,
  onFloorPlanSlideIndexChange,
  onRequestClose,
  onRequestFullscreen,
  enabledTabs,
  views,
}: RoomGalleryViewerProps) {
  const idPrefix = useId()
  const showTopRightControl = mode === 'inline' || mode === 'fullscreen'
  const gallerySlides = useMemo(() => buildApartmentGallerySlides(views), [views])
  const floorPlanSlides = useMemo(
    () => buildApartmentFloorPlanSlides(views.floorPlans),
    [views.floorPlans],
  )
  const activeGallerySlide = gallerySlides[gallerySlideIndex]
  const showComparisonLabels =
    activeView === 'gallery' && activeGallerySlide?.kind === 'comparison'

  const renderPanel = (view: ApartmentGalleryViewId) => {
    if (view === 'gallery') {
      return (
        <RoomGalleryCarousel
          ariaLabel={copy.ariaLabel}
          carouselKey={carouselKey}
          className="rounded-none"
          comparisonCopy={{
            afterLabel: copy.afterLabel,
            beforeLabel: copy.beforeLabel,
          }}
          controlLayout={mode === 'fullscreen' ? 'lightbox' : 'inline'}
          initialSlide={gallerySlideIndex}
          items={gallerySlides}
          nextLabel={copy.next}
          objectFit={objectFit}
          onActiveSlideChange={onGallerySlideIndexChange}
          positionTemplate={copy.position}
          previousLabel={copy.previous}
          resetOnKeyChange={carouselKey}
        />
      )
    }

    if (view === 'view360' && views.view360) {
      return (
        <RoomGalleryVideoPanel
          isActive={activeView === 'view360'}
          objectFit={objectFit}
          tour={views.view360}
        />
      )
    }

    if (view === 'floorPlans' && floorPlanSlides.length > 0) {
      return (
        <RoomGalleryCarousel
          ariaLabel={copy.floorPlansAriaLabel}
          carouselKey={`${carouselKey}-floor-plans`}
          className="rounded-none"
          comparisonCopy={{
            afterLabel: copy.afterLabel,
            beforeLabel: copy.beforeLabel,
          }}
          controlLayout={mode === 'fullscreen' ? 'lightbox' : 'inline'}
          initialSlide={floorPlanSlideIndex}
          items={floorPlanSlides}
          nextLabel={copy.next}
          objectFit={objectFit}
          onActiveSlideChange={onFloorPlanSlideIndexChange}
          positionTemplate={copy.position}
          previousLabel={copy.previous}
          resetOnKeyChange={carouselKey}
        />
      )
    }

    return null
  }

  const getPanelAriaLabel = (view: ApartmentGalleryViewId) => {
    if (view === 'gallery') return copy.ariaLabel
    if (view === 'floorPlans') return copy.floorPlansPanelAriaLabel
    return copy.view360PanelAriaLabel
  }

  return (
    <div
      className={cn(
        'relative size-full',
        mode === 'inline'
          ? 'overflow-hidden rounded-[10px] bg-black/10'
          : 'overflow-visible bg-transparent',
        className,
      )}
    >
      <div
        className={cn(
          'pointer-events-none absolute inset-x-0 z-30 flex min-w-0 items-start gap-2 sm:gap-3',
          mode === 'inline' ? 'top-0 p-2 sm:p-4' : 'bottom-[calc(100%+0.75rem)]',
        )}
      >
        <div className="min-w-0 flex-1 overflow-hidden">
          <RoomGalleryViewTabs
            activeView={activeView}
            ariaLabel={copy.tabsAriaLabel}
            enabledTabs={enabledTabs}
            idPrefix={idPrefix}
            labels={copy.tabs}
            onViewChange={onActiveViewChange}
          />
        </div>

        {showTopRightControl ? (
          <div className="pointer-events-none flex shrink-0 flex-col items-end gap-2">
            {mode === 'inline' && onRequestFullscreen ? (
              <button
                ref={fullscreenButtonRef}
                aria-label={copy.openFullscreen}
                className={galleryOverlayControlClassName}
                onClick={onRequestFullscreen}
                type="button"
              >
                <BsArrowsFullscreen aria-hidden className="size-4" />
              </button>
            ) : null}

            {mode === 'fullscreen' && onRequestClose ? (
              <button
                ref={fullscreenButtonRef}
                aria-label={copy.closeFullscreen}
                className={galleryOverlayControlClassName}
                onClick={onRequestClose}
                type="button"
              >
                <X aria-hidden className="size-4" />
              </button>
            ) : null}

            {mode === 'inline' && showComparisonLabels ? (
              <span aria-hidden className={galleryComparisonLabelClassName}>
                {copy.afterLabel}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>

      {mode === 'inline' && showComparisonLabels ? (
        <span
          aria-hidden
          className={cn(
            galleryComparisonLabelClassName,
            'pointer-events-none absolute left-2 top-[calc(0.5rem+2.75rem+0.5rem)] z-20 sm:left-4 sm:top-[calc(1rem+2.75rem+0.5rem)]',
          )}
        >
          {copy.beforeLabel}
        </span>
      ) : null}

      {mode === 'fullscreen' && showComparisonLabels ? (
        <div className="pointer-events-none absolute inset-x-0 top-4 z-20 flex items-start justify-between px-4">
          <span aria-hidden className={galleryComparisonLabelClassName}>
            {copy.beforeLabel}
          </span>
          <span aria-hidden className={galleryComparisonLabelClassName}>
            {copy.afterLabel}
          </span>
        </div>
      ) : null}

      {enabledTabs.map((view) => {
        const hidden = activeView !== view
        const panelLabel = getPanelAriaLabel(view)

        return (
          <div
            key={view}
            aria-hidden={hidden ? true : undefined}
            aria-labelledby={`${idPrefix}-tab-${view}`}
            className={cn(
              'size-full min-h-0',
              mode === 'fullscreen' &&
                view === 'view360' &&
                'overflow-hidden rounded-[10px] bg-black shadow-2xl',
              hidden && 'hidden',
            )}
            id={`${idPrefix}-panel-${view}`}
            inert={hidden ? true : undefined}
            role="tabpanel"
            {...(hidden ? {} : { 'aria-label': panelLabel })}
          >
            {renderPanel(view)}
          </div>
        )
      })}
    </div>
  )
}
